import { useState, useMemo, useCallback } from 'react';
import { workApprovals } from '@/mocks/workApprovals';
import SearchableSelect from '@/components/base/SearchableSelect';
import DateRangePicker from '@/components/base/DateRangePicker';
import Timeline, { type TimelineItem } from '@/components/feature/Timeline';

const typeMap: Record<string, { label: string; color: string }> = {
  listing: { label: '上架审批', color: 'bg-accent-100 text-accent-700' },
  purchase_plan: { label: '采购计划审批', color: 'bg-primary-100 text-primary-700' },
};

const statusMap: Record<string, { label: string; color: string; icon: string }> = {
  pending: { label: '待审批', color: 'bg-secondary-100 text-secondary-800', icon: 'ri-time-line' },
  approving: { label: '审批中', color: 'bg-accent-100 text-accent-800', icon: 'ri-loader-4-line' },
  approved: { label: '已通过', color: 'bg-primary-100 text-primary-800', icon: 'ri-check-line' },
  rejected: { label: '已驳回', color: 'bg-primary-100 text-primary-800', icon: 'ri-close-circle-line' },
};

const typeFilters = [
  { id: 'all', label: '全部' },
  { id: 'listing', label: '上架审批' },
  { id: 'purchase_plan', label: '采购计划审批' },
];

const allDepartments = Array.from(new Set(workApprovals.map((a) => a.department)));

function buildApprovalTimeline(approval: typeof workApprovals[0]): TimelineItem[] {
  const steps = approval.approvalSteps;
  let currentMarked = false;

  return steps.map((step): TimelineItem => {
    let status: TimelineItem['status'];
    if (step.status === 'approved') {
      status = 'completed';
    } else if (step.status === 'rejected') {
      status = 'rejected';
    } else if (!currentMarked) {
      status = 'current';
      currentMarked = true;
    } else {
      status = 'pending';
    }

    return {
      id: `step-${step.level}`,
      title: step.approverName,
      subtitle: step.role,
      description: step.comment || undefined,
      time: step.approvedAt
        ? new Date(step.approvedAt).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
        : undefined,
      status,
    };
  });
}

export default function WorkApproval() {
  const [activeFilter, setActiveFilter] = useState('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [approvalModal, setApprovalModal] = useState<{ id: string; action: 'approve' | 'reject' } | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [toast, setToast] = useState<string | null>(null);

  // Search / filter state
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedDept, setSelectedDept] = useState('');

  const filteredApprovals = useMemo(() => {
    let result = [...workApprovals];

    if (activeFilter !== 'all') {
      result = result.filter((a) => a.type === activeFilter);
    }

    if (selectedDept) {
      result = result.filter((a) => a.department === selectedDept);
    }

    if (dateFrom) {
      result = result.filter((a) => a.createdAt >= `${dateFrom}T00:00:00Z`);
    }
    if (dateTo) {
      result = result.filter((a) => a.createdAt <= `${dateTo}T23:59:59Z`);
    }

    if (searchKeyword.trim()) {
      const q = searchKeyword.trim().toLowerCase();
      result = result.filter((a) => {
        if (a.title.toLowerCase().includes(q)) return true;
        if (a.applicant.toLowerCase().includes(q)) return true;
        if (a.department.toLowerCase().includes(q)) return true;
        if (a.description.toLowerCase().includes(q)) return true;
        return false;
      });
    }

    return result;
  }, [activeFilter, selectedDept, dateFrom, dateTo, searchKeyword]);

  const hasActiveFilters = searchKeyword || dateFrom || dateTo || selectedDept || activeFilter !== 'all';

  const handleSearch = useCallback(() => {
    setSearchKeyword(searchInput);
  }, [searchInput]);

  const handleReset = useCallback(() => {
    setSearchInput('');
    setSearchKeyword('');
    setDateFrom('');
    setDateTo('');
    setSelectedDept('');
    setActiveFilter('all');
  }, []);

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };

  const handleApprove = (id: string) => setApprovalModal({ id, action: 'approve' });
  const handleReject = (id: string) => { setApprovalModal({ id, action: 'reject' }); setRejectReason(''); };

  const confirmAction = () => {
    if (!approvalModal) return;
    if (approvalModal.action === 'reject' && !rejectReason.trim()) {
      showToast('请填写驳回原因');
      return;
    }
    showToast(approvalModal.action === 'approve' ? '已审批通过' : '已驳回');
    setApprovalModal(null);
    setRejectReason('');
  };

  return (
    <div className="flex-1 bg-background-100">
      {toast && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 rounded-lg bg-accent-500 text-background-50 text-sm font-medium shadow-lg flex items-center gap-2">
          <i className="ri-checkbox-circle-line" />
          {toast}
        </div>
      )}

      <div className="px-4 md:px-6 pb-4 md:pb-6">
        {/* Filter bar — same style as orders page */}
        <div className="bg-white rounded-lg border border-background-200 p-4 mb-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="w-36 shrink-0">
              <SearchableSelect
                options={allDepartments}
                value={selectedDept}
                onChange={(v) => setSelectedDept(typeof v === 'string' ? v : v[0] || '')}
                placeholder="全部科室"
                allLabel="全部科室"
                icon="ri-hospital-line"
                searchPlaceholder="输入过滤科室..."
              />
            </div>

            <div className="w-56 shrink-0">
              <DateRangePicker
                dateFrom={dateFrom}
                dateTo={dateTo}
                onDateFromChange={setDateFrom}
                onDateToChange={setDateTo}
              />
            </div>

            <div className="flex items-center flex-1 min-w-0 max-w-sm border border-background-200 rounded-md overflow-hidden bg-background-50 focus-within:border-primary-400 transition-colors">
              <input
                type="text"
                placeholder="搜索审批标题、申请人、科室"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                className="flex-1 py-2 px-3 text-sm outline-none bg-transparent min-w-0"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={() => { setSearchInput(''); setSearchKeyword(''); }}
                  className="px-2 text-foreground-400 hover:text-foreground-600 shrink-0"
                >
                  <i className="ri-close-line" />
                </button>
              )}
            </div>

            <div className="flex items-center gap-1.5">
              {typeFilters.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setActiveFilter(f.id)}
                  className={`px-2.5 py-1.5 rounded-full text-xs border whitespace-nowrap shrink-0 transition-colors cursor-pointer ${activeFilter === f.id ? 'bg-accent-500 text-background-50 border-accent-500' : 'border-background-300 text-foreground-600 hover:border-accent-400'}`}
                >
                  {f.label}
                  {f.id !== 'all' && <span className="ml-1 opacity-70">({workApprovals.filter((a) => a.type === f.id).length})</span>}
                </button>
              ))}
            </div>

            {hasActiveFilters && (
              <button
                type="button"
                onClick={handleReset}
                className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-background-200 text-xs text-foreground-500 hover:text-foreground-700 hover:bg-background-50 transition-colors shrink-0 whitespace-nowrap"
              >
                <i className="ri-refresh-line" />
                重置
              </button>
            )}

            <span className="ml-auto text-xs text-foreground-500 whitespace-nowrap">
              共 <strong className="text-foreground-900">{filteredApprovals.length}</strong> 条
            </span>
          </div>

          {hasActiveFilters && (
            <div className="flex items-center flex-wrap gap-2 mt-3">
              {searchKeyword && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs bg-primary-50 text-primary-700 border border-primary-200">
                  <i className="ri-search-line" />
                  {searchKeyword}
                  <button
                    type="button"
                    onClick={() => { setSearchInput(''); setSearchKeyword(''); }}
                    className="ml-1 hover:text-primary-900"
                  >
                    <i className="ri-close-line" />
                  </button>
                </span>
              )}
              {selectedDept && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs bg-accent-50 text-accent-700 border border-accent-200">
                  <i className="ri-hospital-line" />
                  {selectedDept}
                  <button
                    type="button"
                    onClick={() => setSelectedDept('')}
                    className="ml-1 hover:text-accent-900"
                  >
                    <i className="ri-close-line" />
                  </button>
                </span>
              )}
              {(dateFrom || dateTo) && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs bg-secondary-50 text-secondary-700 border border-secondary-200">
                  <i className="ri-calendar-line" />
                  {dateFrom || '起始'} ~ {dateTo || '今天'}
                  <button
                    type="button"
                    onClick={() => { setDateFrom(''); setDateTo(''); }}
                    className="ml-1 hover:text-secondary-900"
                  >
                    <i className="ri-close-line" />
                  </button>
                </span>
              )}
            </div>
          )}
        </div>

        {filteredApprovals.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-background-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-file-list-3-line text-2xl text-foreground-400" />
            </div>
            <p className="text-sm text-foreground-500 mb-2">没有找到符合条件的审批</p>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={handleReset}
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                清除筛选条件
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredApprovals.map((approval) => {
              const type = typeMap[approval.type];
              const status = statusMap[approval.status];
              const completedCount = approval.approvalSteps.filter((s) => s.status === 'approved').length;
              const totalCount = approval.approvalSteps.length;

              return (
                <div key={approval.id} className="bg-white rounded-xl border border-background-200 overflow-hidden">
                  {/* Title bar: date left, type+status tags right */}
                  <div className="px-5 py-3 border-b border-background-100 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <i className="ri-calendar-check-line text-accent-500" />
                      <span className="text-sm font-semibold text-foreground-900">
                        工作审批 · {new Date(approval.createdAt).toLocaleDateString('zh-CN', { year: 'numeric', month: 'long', day: 'numeric' })}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${type.color}`}>
                        {type.label}
                      </span>
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${status.color}`}>
                        <i className={status.icon} />
                        {status.label}
                      </span>
                    </div>
                  </div>

                  <div className="p-5">
                    <h3 className="text-sm font-semibold text-foreground-900 mb-2">{approval.title}</h3>
                    <p className="text-sm text-foreground-600 mb-3 leading-relaxed">{approval.description}</p>

                    <div className="flex items-center gap-4 text-xs text-foreground-500 flex-wrap mb-3">
                      <span>申请人：{approval.applicant}</span>
                      <span>科室：{approval.department}</span>
                      {approval.relatedInfo && <span>{approval.relatedInfo}</span>}
                    </div>

                    {/* Bottom row: progress summary + action buttons */}
                    <div className="flex items-center justify-between pt-3 border-t border-background-100">
                      <div className="flex items-center gap-1.5 text-xs">
                        <i className={`${status.icon} ${status.color.replace('bg-', 'text-').replace('-100', '-600').replace('-800', '-600')}`} />
                        <span className="text-foreground-500">
                          审批进度
                          <span className="text-foreground-700 font-medium ml-1">{completedCount}/{totalCount}</span>
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {(approval.status === 'pending' || approval.status === 'approving') && (
                          <>
                            <button
                              onClick={() => handleApprove(approval.id)}
                              className="px-3 py-1.5 rounded-lg border border-accent-200 text-xs text-accent-600 hover:bg-accent-50 font-medium transition-colors whitespace-nowrap cursor-pointer"
                            >
                              <i className="ri-check-line mr-1" /> 同意
                            </button>
                            <button
                              onClick={() => handleReject(approval.id)}
                              className="px-3 py-1.5 rounded-lg border border-primary-200 text-xs text-primary-600 hover:bg-primary-50 font-medium transition-colors whitespace-nowrap cursor-pointer"
                            >
                              <i className="ri-close-line mr-1" /> 驳回
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => toggleExpand(approval.id)}
                          className="px-3 py-1.5 rounded-lg border border-background-200 text-xs text-foreground-600 hover:bg-background-50 font-medium flex items-center gap-1 whitespace-nowrap cursor-pointer"
                        >
                          {expandedId === approval.id ? '收起流程' : '查看审批流程'}
                          <i className={expandedId === approval.id ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Expanded timeline */}
                  {expandedId === approval.id && (
                    <div className="border-t border-background-200 px-4 md:px-6 py-6 bg-background-50">
                      <h4 className="text-sm font-semibold text-foreground-900 mb-5">审批流程</h4>
                      <Timeline items={buildApprovalTimeline(approval)} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Modal */}
        {approvalModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setApprovalModal(null)}>
            <div className="bg-background-50 rounded-xl w-full max-w-md mx-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <div className="px-5 py-4 border-b border-background-200">
                <h3 className="text-sm font-semibold text-foreground-900">
                  {approvalModal.action === 'approve' ? '确认审批通过' : '驳回申请'}
                </h3>
              </div>
              <div className="px-5 py-4">
                {approvalModal.action === 'approve' ? (
                  <p className="text-sm text-foreground-600">确认同意该审批申请？</p>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-foreground-600">请填写驳回原因：</p>
                    <textarea
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      placeholder="请说明驳回原因..."
                      maxLength={500}
                      className="w-full px-3 py-2.5 text-sm border border-background-300 rounded-lg outline-none focus:border-primary-400 resize-none min-h-[80px]"
                    />
                  </div>
                )}
              </div>
              <div className="px-5 py-4 border-t border-background-200 flex justify-end gap-2">
                <button onClick={() => setApprovalModal(null)} className="px-4 py-2 rounded-lg border border-background-300 text-foreground-600 text-sm hover:bg-background-100">取消</button>
                <button onClick={confirmAction} className={`px-4 py-2 rounded-lg text-sm font-medium text-background-50 ${approvalModal.action === 'approve' ? 'bg-accent-500 hover:bg-accent-600' : 'bg-primary-500 hover:bg-primary-600'}`}>
                  {approvalModal.action === 'approve' ? '确认同意' : '确认驳回'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}